import React from 'react';
import { Layout, Empty, Row, Col, Typography, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import MessageList from './MessageList/MessageList';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import { useTranslation } from 'react-i18next';

const { Content } = Layout;
const { Text } = Typography;

// Typing animation keyframes
const typingAnimationStyle = `
  @keyframes typingAnimation {
    0% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
    100% { transform: translateY(0); }
  }
`;

// Enhanced Typing indicator component with local styles - positioned on right (agent) side
const TypingIndicator = ({ isDarkMode }) => {
    const { t } = useTranslation();

    const bubbleStyle = {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        backgroundColor: isDarkMode ? '#303030' : '#f0f0f0',
        borderRadius: '18px',
        padding: '10px 16px',
        maxWidth: '80%',
        marginRight: '12px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    };

    const bubblePointerStyle = {
        content: '""',
        position: 'absolute',
        bottom: '8px',
        right: '-6px',
        width: '12px',
        height: '12px',
        backgroundColor: isDarkMode ? '#303030' : '#f0f0f0',
        transform: 'rotate(45deg)',
        borderRadius: '2px',
    };

    const dotBaseStyle = {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: '#1890ff',
        display: 'inline-block',
        margin: '0 2px',
    };

    return (
        <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            position: 'absolute',
            bottom: 10,
            right: 10,
            zIndex: 2,
            justifyContent: 'flex-end',
            width: 'calc(100% - 20px)'
        }}>
            <style dangerouslySetInnerHTML={{ __html: typingAnimationStyle }} />
            <div style={bubbleStyle}>
                <div style={{ ...bubblePointerStyle }} />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div 
                        style={{ 
                            ...dotBaseStyle, 
                            animation: 'typingAnimation 1s infinite ease-in-out',
                            animationDelay: '0s'
                        }} 
                    />
                    <div 
                        style={{ 
                            ...dotBaseStyle, 
                            animation: 'typingAnimation 1s infinite ease-in-out',
                            animationDelay: '0.2s'
                        }} 
                    />
                    <div 
                        style={{ 
                            ...dotBaseStyle, 
                            animation: 'typingAnimation 1s infinite ease-in-out',
                            animationDelay: '0.4s'
                        }} 
                    />
                    <Text style={{ marginLeft: '8px', color: '#1890ff' }}>
                        {t('agent_is_typing')}
                    </Text>
                </div>
            </div>
            <Avatar
                size="small"
                icon={<UserOutlined />}
                style={{ marginLeft: '8px' }}
            />
        </div>
    );
};

const MessageContent = ({
    screens,
    selectedConversation,
    messages,
    loading,
    messageRefs,
    handleScroll,
    handleReplyClick,
    setSelectedMessage,
    setDrawerVisible,
    hasMore
}) => {
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const contentBackgroundColor = isDarkMode ? '#1f1f1f' : '#fff';
    const isWriting = selectedConversation?.is_writing;

    return (
        <div style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
            <Content
                className="message-container"
                theme={isDarkMode ? 'dark' : 'light'}
                onScroll={handleScroll}
                style={contentStyle(screens, contentBackgroundColor, isWriting)}
            >
                {!selectedConversation || !messages.length ? (
                    <EmptyState isDarkMode={isDarkMode} />
                ) : (
                    <>
                        <MessageList
                            messages={messages}
                            loading={loading}
                            messageRefs={messageRefs}
                            handleReplyClick={handleReplyClick}
                            setSelectedMessage={setSelectedMessage}
                            setDrawerVisible={setDrawerVisible}
                        />
                    </>
                )}
            </Content>
            {isWriting && selectedConversation && messages.length > 0 && (
                <TypingIndicator isDarkMode={isDarkMode} />
            )}
        </div>
    );
};

const contentStyle = (screens, contentBackgroundColor, isWriting) => ({
    padding: screens.xs ? 12 : 24,
    paddingBottom: isWriting ? '60px' : (screens.xs ? 12 : 24), // More padding for typing indicator
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column-reverse',
    height: '100%',
    backgroundColor: contentBackgroundColor,
});

const EmptyState = ({ isDarkMode }) => {
    const { t } = useTranslation();
    return (
    <Row justify="center" align="middle" style={{ height: '100%' }}>
        <Col>
            <Empty
                description={t('select_conversation_to_view_messages')}
                imageStyle={{
                    filter: isDarkMode ? 'invert(0.8)' : 'none'
                }}
            />
        </Col>
    </Row>
    );
};

export default MessageContent;
